import React, { useEffect, useRef, useState } from 'react';
import { jsonEditorStyles } from '../jsonEditorStyles';
import Button from '@mui/material/Button';
import cn from 'clsx';
import useComponentUIHook from './useComponentUIHook';
import TemplatizedFieldComponent, { TemplatizedValue, getTemplatizedValue } from './templatizedFieldComponent';
import SessionManagement, { sessionFieldsAreValid } from './sessionManagement';
import { FieldMapperRow } from './fieldMapperRow';
import { OptionProps } from '../../../view-models';
import { getTemplatizationFields } from '../../../store';
import { useSelector } from 'react-redux';
import { Typography } from '@mui/material';
import { guidGenerator } from '../../../utils/utils';
import { cloneDeep } from 'lodash';
import jsonComponents from '../../../constants/jsonComponents';
import { claimFunctions } from '../../../jsonParsing/mappedClaimTransforms/mappedClaimTransforms';

interface componentProps {
    componentData: any;
    closeGritComponent: any;
    setUnsavedChanges: any;
}
  
const GenerateSetClaimsComponentUI = ({
    componentData,
    closeGritComponent,
    setUnsavedChanges,
}: componentProps) => {
    const { modifiedData, setModifiedData, checkIfSameIgnoringSubFlows, allClaims, modifiedTemplatizations, setModifiedTemplatizations } = useComponentUIHook(componentData);

    const configData = jsonComponents.setClaims.data;

    const [claimsList, setClaimsList] = useState<OptionProps[]>([]);
    const templatizationFields = useSelector(getTemplatizationFields);
    const [disableSubmit, setDisableSubmit] = useState<boolean>(false);

    const [fieldList, setFieldList] = useState<OptionProps[]>(Object.keys(templatizationFields).map((fKey) => { return { value: fKey, label: `{settings:${templatizationFields[fKey].name}}` } }));
    useEffect(() => {
        setFieldList(Object.keys(templatizationFields).map((fKey) => { return { value: fKey, label: `{settings:${templatizationFields[fKey].name}}` } }));
    }, [templatizationFields]);

    const [nextSetMapping, setNextSetMapping] = useState({
        destClaim: '',
        sourceType: '',
        sourceValue: '',
        defaultValue: ''
    });

    useEffect(() => {
        if (Object.keys(allClaims).length > 0) {
            const _temp = Object.keys(allClaims)
                .reduce((acc: OptionProps[], curVal: string) => { if ((!['password', 'foreignId'].includes(curVal)) && (['string', 'boolean'].includes(allClaims[curVal]?.dataType))) { return acc.concat({ label: curVal, value: curVal }) }; return acc; }, [])
                .sort((a: OptionProps, b: OptionProps) => (a.value.toLowerCase() > b.value.toLowerCase() ? 1 : -1));
            setClaimsList(_temp);
        }
    }, [allClaims])

    useEffect(() => {
        const _res: boolean = checkIfSameIgnoringSubFlows();
        setDisableSubmit(_res ||
            Object.keys(modifiedData['setClaims']).some((scId: string) => {
                return (!modifiedData['setClaims'][scId]['destClaim']) ||
                    (!modifiedData['setClaims'][scId]['sourceType']) ||
                    ((modifiedData['setClaims'][scId]['sourceType'] !== 'literal') && (!modifiedData['setClaims'][scId]['sourceValue'])) ||
                    ((modifiedData['setClaims'][scId]['sourceType'] === 'claim') && (allClaims[modifiedData['setClaims'][scId]['destClaim']]?.dataType !== allClaims[modifiedData['setClaims'][scId]['sourceValue']?.['value']]?.dataType));
            }) ||
            (!sessionFieldsAreValid(modifiedData)));
        setUnsavedChanges(_res);
    }, [modifiedData]);
    
    const classes = jsonEditorStyles();

    return (
        <div className={classes.flexCol}>
            <Typography>
                Claims to set:
            </Typography>
            {Object.keys(modifiedData['setClaims']).map((scId: string, idx: number) => {
                return (
                    <FieldMapperRow
                        key={idx}
                        rowData={{ ...modifiedData['setClaims'][scId], sourceValue: modifiedData['setClaims'][scId]['sourceValue']['value'] }}
                        writeback={(rowData) => {
                            // FieldMapperRow won't handle templatization state updates, so need to take care of it if appsetting is selected.
                            if (modifiedData['setClaims'][scId]['sourceType'] === 'appsetting') {
                                if (rowData['sourceType'] !== 'appsetting') {
                                    setModifiedTemplatizations({
                                        ...modifiedTemplatizations,
                                        ['setClaims.' + scId + '.sourceValue']: null
                                    })
                                } else if (rowData['sourceValue'] !== modifiedData['setClaims'][scId]['sourceValue']['value']) {
                                    setModifiedTemplatizations({
                                        ...modifiedTemplatizations,
                                        ['setClaims.' + scId + '.sourceValue']: rowData['sourceValue']
                                    });
                                }
                            } else if (rowData['sourceType'] === 'appsetting') {
                                setModifiedTemplatizations({
                                    ...modifiedTemplatizations,
                                    ['setClaims.' + scId + '.sourceValue']: rowData['sourceValue']
                                });
                            }
                            setModifiedData({
                                ...modifiedData,
                                setClaims: {
                                    ...modifiedData['setClaims'],
                                    [scId]: {
                                        ...rowData,
                                        sourceValue: {
                                            value: rowData['sourceValue'],
                                            templatized: rowData['sourceType'] === 'appsetting'
                                        }
                                    }
                                }
                            });
                        }}
                        claimsList={claimsList.filter((cl) => {
                            return (cl.value !== modifiedData['setClaims'][scId]['destClaim']) && (allClaims[cl.value]?.dataType === allClaims[modifiedData['setClaims'][scId]['destClaim']]?.dataType)
                        })}
                        allClaims={allClaims}
                        enabledSources={allClaims[modifiedData['setClaims'][scId]['destClaim']]?.dataType === 'string' ? ['claim', 'literal', 'resolver', 'appsetting', 'transform'] : ['claim', 'literal', 'appsetting']}
                        precursorFields={[
                            {
                                fieldName: 'destClaim',
                                fieldType: "select",
                                fieldData: {
                                    label: 'Claim to Set',
                                    value: modifiedData['setClaims'][scId]['destClaim'],
                                    options: claimsList.filter((cl) => {
                                        return ((cl.value === modifiedData['setClaims'][scId]['destClaim'])
                                            || (!(Object.keys(modifiedData['setClaims']).some((oscId: string) => { return modifiedData['setClaims'][oscId]['destClaim'] === cl.value }))))
                                            && ((modifiedData['setClaims'][scId]['sourceType'] !== 'claim') || (modifiedData['setClaims'][scId]['sourceValue'] !== cl.value))
                                    }),
                                    required: true,
                                    error: !modifiedData['setClaims'][scId]['destClaim'],
                                    helperText: modifiedData['setClaims'][scId]['destClaim'] ? '' : 'Value must not be empty',
                                    style: { width: '500px', margin: 1 }
                                }
                            }
                        ]}
                        literalDataType={allClaims[modifiedData['setClaims'][scId]['destClaim']]?.dataType}
                        useBaseResolversOnly={true}
                        sourceTypeFieldName='sourceType'
                        sourceTypeFieldLabel='Set From'
                        sourceFieldName='sourceValue'
                        sourceFieldLabels={{ 'claim': 'Source Claim', 'literal': 'Literal', 'resolver': 'Session Context', 'appsetting': 'App Setting', 'transform': 'Function' }}
                        defaultValueFieldName={((allClaims[modifiedData['setClaims'][scId]['destClaim']]?.dataType === 'string') && (allClaims[modifiedData['setClaims'][scId]['sourceValue']['value']]?.dataType === 'string')) ? 'defaultValue' : ''}
                        defaultValueFieldLabel='Default Value'
                        allowEmptyDefaultValue={true}
                        buttonText='Delete'
                        buttonAction={() => {
                            if (modifiedData['setClaims'][scId]['sourceType'] === 'appsetting') {
                                setModifiedTemplatizations({
                                    ...modifiedTemplatizations,
                                    ['setClaims.' + scId + '.sourceValue']: null
                                });
                            }
                            setModifiedData({
                                ...modifiedData,
                                setClaims: {
                                    ...Object.keys(modifiedData['setClaims']).reduce((acc: { [index: string]: any }, scd) => {
                                        if (scd !== scId) {
                                            acc[scd] = { ...modifiedData['setClaims'][scd] }
                                        }
                                        return acc;
                                    }, {})
                                }
                            });
                        }}
                        templateFieldList={fieldList}
                        transformOptions={[
                            { label: 'String Format', value: claimFunctions.stringFormat}
                        ]}
                        transformFieldName='transformData'
                    />
                );
            })}
            <FieldMapperRow
                rowData={nextSetMapping}
                writeback={(rowData) => {
                    setNextSetMapping({
                        ...rowData
                    });
                }}
                claimsList={claimsList.filter((cl) => {
                    return (cl.value !== nextSetMapping['destClaim']) && (allClaims[cl.value]?.dataType === allClaims[nextSetMapping['destClaim']]?.dataType)
                })}
                allClaims={allClaims}
                enabledSources={allClaims[nextSetMapping['destClaim']]?.dataType === 'string' ? ['claim', 'literal', 'resolver', 'appsetting', 'transform'] : ['claim', 'literal', 'appsetting']}
                precursorFields={[
                    {
                        fieldName: 'destClaim',
                        fieldType: "select",
                        fieldData: {
                            label: 'Claim to Set',
                            value: nextSetMapping['destClaim'],
                            options: claimsList.filter((cl) => {
                                return (!(Object.keys(modifiedData['setClaims']).some((oscId: string) => { return modifiedData['setClaims'][oscId]['destClaim'] === cl.value })))
                                    && ((nextSetMapping['sourceType'] !== 'claim') || (nextSetMapping['sourceValue'] !== cl.value))
                            }),
                            style: { width: '500px', margin: 1}
                        }
                    }
                ]}
                literalDataType={allClaims[nextSetMapping['destClaim']]?.dataType}
                useBaseResolversOnly={true}
                sourceTypeFieldName='sourceType'
                sourceTypeFieldLabel='Set From'
                sourceFieldName='sourceValue'
                sourceFieldLabels={{ 'claim': 'Source Claim', 'literal': 'Literal', 'resolver': 'Session Context', 'appsetting': 'App Setting', 'transform': 'Function' }}
                defaultValueFieldName={((allClaims[nextSetMapping['destClaim']]?.dataType === 'string') && (allClaims[nextSetMapping['sourceValue']]?.dataType) === 'string') ? 'defaultValue' : ''}
                defaultValueFieldLabel='Default Value'
                allowEmptyDefaultValue={true}
                buttonText='Add'
                buttonAction={() => {
                    const newScId = guidGenerator();
                    if (nextSetMapping['sourceType'] === 'appsetting') {
                        setModifiedTemplatizations({
                            ...modifiedTemplatizations,
                            ['setClaims.' + newScId + '.sourceValue']: nextSetMapping['sourceValue']
                        });
                    }
                    setModifiedData({
                        ...modifiedData,
                        setClaims: {
                            ...modifiedData['setClaims'],
                            [newScId]: {
                                ...nextSetMapping,
                                sourceValue: { value: nextSetMapping['sourceValue'], templatized: nextSetMapping['sourceType'] === 'appsetting' }
                            }
                        }
                    });
                    setNextSetMapping({
                        destClaim: '',
                        sourceType: '',
                        sourceValue: '',
                        defaultValue: ''
                    });
                }}
                buttonDisabled={
                    (!nextSetMapping.destClaim) ||
                    (!nextSetMapping.sourceType) ||
                    ((nextSetMapping.sourceType !== 'literal') && (!nextSetMapping.sourceValue)) ||
                    ((nextSetMapping.sourceType === 'claim') && (allClaims[nextSetMapping['destClaim']]?.dataType !== allClaims[nextSetMapping['sourceValue']]?.dataType)) ||
                    ((nextSetMapping.sourceType === 'resolver') && (allClaims[nextSetMapping['destClaim']]?.dataType !== 'string')) ||
                    ((nextSetMapping.sourceType === 'transform') && (allClaims[nextSetMapping['destClaim']]?.dataType !== 'string'))
                }
                templateFieldList={fieldList}
                transformOptions={[
                    { label: 'String Format', value: claimFunctions.stringFormat}
                ]}
                transformFieldName='transformData'
            />
            {configData.sessionManagement && (
                <SessionManagement
                    modifiedData={modifiedData}
                    changeHandler={(data: any) => setModifiedData(data)}
                    sessionTypes={configData.sessionTypes}
                />
            )}
            <div>
                <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={disableSubmit}
                    sx={{ mt: 3, mb: 2 }}
                    onClick={() => {
                        closeGritComponent(cloneDeep(modifiedData), modifiedTemplatizations);
                    }}
                >
                    Save
                </Button>
            </div>
        </div>
    )
    
    

}

export default GenerateSetClaimsComponentUI;